import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/Button'
import { AlertTriangle, Clock } from 'lucide-react'

export const InactivityWarningModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(5 * 60) // 5 minutes in seconds
  const onExtendRef = useRef<(() => void) | null>(null)
  const countdownRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const handleInactivityWarning = (event: Event) => {
      const customEvent = event as CustomEvent<{
        timeRemaining: number
        onExtend: () => void
      }>

      setIsOpen(true)
      setTimeRemaining(Math.floor(customEvent.detail.timeRemaining / 1000)) // Convert to seconds
      onExtendRef.current = customEvent.detail.onExtend

      // Start countdown
      if (countdownRef.current) {
        clearInterval(countdownRef.current)
      }

      countdownRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up - modal will close, logout will happen
            setIsOpen(false)
            if (countdownRef.current) {
              clearInterval(countdownRef.current)
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    window.addEventListener('inactivity-warning', handleInactivityWarning)

    return () => {
      window.removeEventListener('inactivity-warning', handleInactivityWarning)
      if (countdownRef.current) {
        clearInterval(countdownRef.current)
      }
    }
  }, [])

  const handleStayLoggedIn = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
    }
    setIsOpen(false)
    if (onExtendRef.current) {
      onExtendRef.current()
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-surface rounded-lg shadow-2xl max-w-md w-full mx-4 p-6 border-2 border-amber-500">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full">
            <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-strong mb-2">Still There?</h2>
            <p className="text-muted text-sm">
              You've been inactive for a while. For your security, you'll be automatically logged out soon.
            </p>
          </div>
        </div>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-6">
          <div className="flex items-center justify-center gap-3">
            <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="text-xs text-amber-700 dark:text-amber-300 uppercase tracking-wide font-medium">
                Time Remaining
              </p>
              <p className="text-3xl font-bold text-amber-900 dark:text-amber-100 tabular-nums">
                {formatTime(timeRemaining)}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleStayLoggedIn}
            className="w-full"
            size="lg"
          >
            Stay Logged In
          </Button>
          <p className="text-xs text-center text-subtle">
            Any activity will reset the timer
          </p>
        </div>
      </div>
    </div>
  )
}
